import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Button,
    Checkbox,
    Flex,
    Highlight,
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { useTranslation } from 'react-i18next';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { IconArrowRight, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useGetQuestionnaires } from '../../../global/entities/Questionnaires/queries/useGetQuestionnaires.tsx';
import { Questionnaires } from '../../../global/entities/Questionnaires/Questionnaires.ts';
import { useDeleteQuestionnaire } from '../../../global/entities/Questionnaires/mutations/useDeleteQuestionnaireMutations.tsx';
import { modals } from '@mantine/modals';

const SurveyTable: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const { data: questionnairesData, isLoading, refetch } = useGetQuestionnaires();
        const { mutateAsync: deleteQuestionnaire } = useDeleteQuestionnaire();
        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();
        const [search, setSearch] = useState('');
        const navigate = useNavigate();

        useEffect(() => {
            setSortedData([]);
            if (questionnairesData?.length) 
                setSortedData(questionnairesData);
            
        }, [questionnairesData]);

        const onClickDelete = (questionnaireId: string) => {
            deleteQuestionnaire(questionnaireId)
                .then(() => {
                    refetch();
                })
                .catch((error) => {
                    console.error('Erreur lors de la suppression :', error);
                })
                .finally(() => {});
        };

        const openModalDelete = (surveyId: string) =>
            modals.openConfirmModal({
                title: t('modal.deleteSurveyModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteSurveyModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteSurveyModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => {
                    onClickDelete(surveyId);
                },
                size: 'lg',
                radius: 'xs',
            });

        if (isLoading) 
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button onClick={() => navigate('/adminDashboard/hcp')} variant="transparent">
                        <Title c="black" order={4}>
                            {t('formSurvey.titre')}
                        </Title>
                    </Button>
                    <Button
                        onClick={() => navigate('/adminDashboard/survey/0')}
                        leftSection={<IconPlus />}
                        styles={{ label: { align: 'center', justifyContent: 'center' } }}
                    >
                        {t('formSurvey.addQuestionnaire')}
                    </Button>
                </Flex>
                <Paper w="100%" h={'min-content'}>
                    <Stack gap={'md'} h={'100%'}>
                        <TextInput
                            placeholder={t('global.search')}
                            rightSection={<IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            width={'100%'}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <ScrollArea w="100%">
                            <Table verticalSpacing="xs">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'shortName'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('shortName', sortedData)}
                                            width={150}
                                        >
                                            {t('formSurvey.shortName')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'name'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('name', sortedData)}
                                        >
                                            {t('formGlobal.name')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'showInMedicalOverview'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('showInMedicalOverview', sortedData)}
                                            width={'min-content'}
                                        >
                                            {t('formSurvey.showInMedicalOverview')}
                                        </Th>
                                        <Table.Th w={50}></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData?.map((questionnaire: Questionnaires, index: number) => {
                                        const isSearch =
                                            containsStringIgnoreCase(
                                                [questionnaire.name, questionnaire.shortName],
                                                search,
                                            ) || !search;
                                        return (
                                            <Table.Tr
                                                key={questionnaire.id}
                                                hidden={!isSearch}
                                                style={{
                                                    background:
                                                        index % 2 === 1
                                                            ? undefined
                                                            : 'var(--Background-Gray-light, #E9ECEF)',
                                                    pointer: 'cursor',
                                                }}
                                            >
                                                <Table.Td
                                                    style={{
                                                        maxWidth: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    maw={150}
                                                >
                                                    <Highlight
                                                        maw={150}
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {questionnaire.shortName}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td
                                                    style={{
                                                        maxWidth: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {questionnaire.name}
                                                    </Highlight>
                                                </Table.Td>

                                                <Table.Td width={50}>
                                                    <Flex justify="center" align="center" h="100%">
                                                        <Checkbox
                                                            checked={questionnaire.showInMedicalOverview}
                                                            readOnly
                                                        />
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex align={'center'}>
                                                        <ActionIcon
                                                            tabIndex={-1}
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => openModalDelete(questionnaire.id.toString())}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>

                                                        <ActionIcon variant="transparent">
                                                            {isLoading ? (
                                                                <Loader
                                                                    size="xs"
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            ) : (
                                                                <IconArrowRight
                                                                    color={theme.colors?.blue?.toString()}
                                                                    onClick={() => {
                                                                        navigate(
                                                                            `/adminDashboard/survey/${questionnaire.id}`,
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                        </ActionIcon>
                                                    </Flex>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Stack>
                </Paper>
            </>
        );
    }),
);

const WrappedSurveyTable: React.FC = () => {
    return (
        <TableContextProvider>
            <SurveyTable />
        </TableContextProvider>
    );
};

export default WrappedSurveyTable;
